import React, {Component} from 'react'
import propTypes from 'prop-types'
import {isEmpty} from 'lodash'
import MuiDialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Icon, {iconNames} from 'components/icon'
import {prefix} from 'helpers/component'
import themeVars from 'theme/themeVars'

const cn = prefix('dialog-')
require('./dialog.scss')

const getStyles = (props) => {
  const {width, minHeight, style, contentStyle, bodyStyle, overlayStyle, isMaximised} = props

  return {
    dialog: isMaximised
      ? {...style, height: '100vh', width: '100vw', margin: 0}
      : style,
    dialogPaper: isMaximised
      ? {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        margin: 0,
        ...contentStyle,
      }
      : {width: width, maxWidth: '90vw', ...contentStyle},
    dialogContent: {
      padding: 0,
      minHeight: isMaximised ? '100%' : minHeight,
      overflowY: 'auto',
      overflowX: 'hidden',
      ...bodyStyle,
    },
    dialogBackdrop: overlayStyle,
  }
}

export class Dialog extends Component {
  componentDidMount() {
    if (this.props.visible) this.init()
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.visible && this.props.visible) this.init()
    if (prevProps.visible && !this.props.visible) this.deinit()
  }

  componentWillUnmount() {
    this.deinit()
  }

  init = () => {
    window.addEventListener("keydown", this.keyDown)
  }

  deinit = () => {
    window.removeEventListener("keydown", this.keyDown)
  }

  close = (...p) => {
    const {onClose, onRequestClose} = this.props
    if (onClose) onClose(...p)
    if (onRequestClose) onRequestClose(...p)
  }

  keyDown = (e) => {
    const {onDefault, canClose} = this.props
    switch (e.keyCode) {
      case 13:  // ENTER
        if (onDefault) onDefault(e)
        break
      case 27:  // ESC
        if (canClose) this.close(e)
        break
    }
  }

  render() {
    const {
      visible,
      title,
      modal,
      showHeader,
      headerRightContent,
      headerStyle,
      canClose,
      actions,
      padded,
      className,
      contentClassName,
      bodyClassName,
      innerStyle,
      showMaximiseButton,
      isMaximised,
      onMaximiseJobCardDialogView,
      onRestoreJobCardDialogView,
      centerTitle,
      ...dialogProps
    } = this.props

    let {headerContent} = this.props
    const styles = getStyles(this.props)

    delete dialogProps["width"]
    delete dialogProps["minHeight"]
    delete dialogProps["style"]
    delete dialogProps["contentStyle"]
    delete dialogProps["bodyStyle"]
    delete dialogProps["overlayStyle"]
    delete dialogProps["headerContent"]
    delete dialogProps["centerTitle"]
    delete dialogProps["onRequestClose"]
    delete dialogProps["onDefault"]
    delete dialogProps["contentClassName"]
    delete dialogProps["bodyClassName"]
    delete dialogProps["autoScrollBodyContent"]
    delete dialogProps["loading"]

    let header, inlineCloseButton, inlineTitle, inlineMaximiseButton
    if (showHeader) {
      if (!headerContent) {
        const closeButton = canClose
          ? (
            <div className={cn('header-close')} onClick={this.close} style={styles.close}>
              <Icon name="close" color={themeVars.palette.whiteColor}/>
            </div>
          )
          : null
        headerContent = [
          <div key="title" className={cn("header-title")} style={centerTitle ? {textAlign: 'center'} : undefined}>
            {title}
          </div>,
          <div key="right" className={cn("header-right")}>
            {headerRightContent}
            {closeButton}
          </div>,
        ]
      }
      header = (
        <div className={cn("header")} style={headerStyle}>
          {headerContent}
        </div>
      )
    } else {
      if (title) {
        inlineTitle = (
          <div className={cn('inline-title')} style={centerTitle ? {textAlign: 'center'} : undefined}>
            {title}
          </div>
        )
      }
      if (showMaximiseButton) {
        inlineMaximiseButton =
          <div
            className={cn("inline-maximise")}
            onClick={isMaximised ? onRestoreJobCardDialogView : onMaximiseJobCardDialogView}
            title={isMaximised ? "Restore" : "Maximise"}
          >
            <Icon
              name={isMaximised ? iconNames.restoreDefaultView : iconNames.maximise}
              size={20}>
            </Icon>
          </div>
      }
      if (canClose) {
        inlineCloseButton =
          <div className={cn("inline-close")} onClick={this.close}>
            <Icon name="close"/>
          </div>
      }
    }

    const innerClasses = ['inner']
    if (padded) {
      innerClasses.push('inner-padded')
    }

    return (
      <MuiDialog
        key={'_Dialog_' + title}
        open={visible}
        onClose={this.close}
        disableBackdropClick={modal}
        disableEscapeKeyDown={modal}
        disableRestoreFocus={!modal}
        disableEnforceFocus={true}
        maxWidth={false}
        className={`${cn('container')} ${className}`}
        classes={{
          paper: `${cn('content')} ${contentClassName || ''}`,
        }}
        PaperProps={{
          style: styles.dialogPaper,
        }}
        BackdropProps={{
          style: styles.dialogBackdrop,
        }}
        style={styles.dialog}
        {...dialogProps}
      >
        {header}
        <DialogContent
          className={`${cn('body')} ${bodyClassName || ''}`}
          style={styles.dialogContent}
          tabIndex={0}
        >
          <div className={cn(innerClasses)} style={innerStyle}>
            {inlineTitle}
            {visible && this.props.children}
            {inlineMaximiseButton}
            {inlineCloseButton}
          </div>
        </DialogContent>
        {!isEmpty(actions) && (
          <DialogActions className={cn('footer')}>
            {actions}
          </DialogActions>
        )}
      </MuiDialog>
    )
  }
}

Dialog.propTypes = {
  visible: propTypes.bool.isRequired,
  className: propTypes.string,
  contentClassName: propTypes.string,
  bodyClassName: propTypes.string,
  width: propTypes.number,
  minHeight: propTypes.number,
  title: propTypes.any,
  /* The buttons at the bottom of the Dialog pop e.g. add, cancel*/
  actions: propTypes.oneOfType([propTypes.array, propTypes.node]),
  headerRightContent: propTypes.any,
  canClose: propTypes.bool,
  showHeader: propTypes.bool,
  children: propTypes.any,
  onDefault: propTypes.func,
  onClose: propTypes.func,
  onRequestClose: propTypes.func, // for backwards-compatibility
  headerContent: propTypes.array,
  headerStyle: propTypes.object,
  padded: propTypes.bool,
  style: propTypes.object,
  contentStyle: propTypes.object,
  bodyStyle: propTypes.object,
  innerStyle: propTypes.object,
  overlayStyle: propTypes.object,
  modal: propTypes.bool,
  showMaximiseButton: propTypes.bool,
  onMaximiseJobCardDialogView: propTypes.func,
  onRestoreJobCardDialogView: propTypes.func,
  isMaximised: propTypes.bool,
  centerTitle: propTypes.bool,
}

Dialog.defaultProps = {
  className: '',
  canClose: true,
  showHeader: true,
  modal: false,
  width: 650,
  minHeight: 300,
  actions: [],
  showMaximiseButton: false,
  isMaximised: false,
  centerTitle: false,
}

export default Dialog
